import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Image as ImageIcon, Lightbulb, ListPlus, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useEntitlements } from '../context/EntitlementsContext';
import { cn } from '@/lib/utils';

// + Capture (web app v4 plan §5.1): one way in for anything new. Each item
// opens today's create or upload form on its page; what's captured is then
// waiting in Review. `n` still opens a new work item.

export interface CaptureItem {
  id: string;
  label: string;
  hint: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const CAPTURE_ITEMS: readonly (CaptureItem & { entitlement?: 'transcriptProcessing' | 'imageAnalysis' })[] = [
  { id: 'work-item', label: 'New work item', hint: 'Shortcut: n', to: '/work?action=create', icon: ListPlus },
  { id: 'idea', label: 'Idea', hint: 'Park it in Review', to: '/review/ideas?action=capture', icon: Lightbulb },
  { id: 'transcript', label: 'Transcript', hint: 'Find the actions in a meeting', to: '/review/captured?type=transcript&action=upload', icon: FileText, entitlement: 'transcriptProcessing' },
  { id: 'image', label: 'Image', hint: 'A whiteboard, a note, a screenshot', to: '/review/captured?type=image&action=upload', icon: ImageIcon, entitlement: 'imageAnalysis' },
];

/** The capture items this person's plan includes (unknown counts as included; the page still checks). */
export function useCaptureItems(): CaptureItem[] {
  const { known } = useEntitlements();
  return CAPTURE_ITEMS.filter(i => !i.entitlement || known(i.entitlement) !== false);
}

export function CaptureMenu({ className, onNavigate }: { className?: string; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const items = useCaptureItems();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center gap-2.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors',
            'hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className,
          )}
        >
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          Capture
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" aria-label="Capture" className="w-64 p-1.5">
        <ul className="flex flex-col gap-0.5">
          {items.map(item => {
            const Icon = item.icon;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => { setOpen(false); onNavigate?.(); navigate(item.to); }}
                  className="flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-foreground/70" aria-hidden />
                  <span className="flex min-w-0 flex-col">
                    <span className="font-medium">{item.label}</span>
                    <span className="text-xs text-muted-foreground">{item.hint}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
